import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getStripePlans } from "../api";
import { MODULE_KEYS, MODULE_LABELS, MODULE_COLORS, CORE_LABEL, ModuleKey } from "../moduleMetadata";
import { CORE_FEATURES, MODULE_FEATURES, MODULE_PITCH } from "../moduleCatalogue";

const NAVY  = "#1e3a8a";
const BLUE  = "#2563eb";
const SKY   = "#0ea5e9";
const LIGHT = "#f0f7ff";
const GREEN = "#059669";

/** Presentation only. Everything numeric comes from /api/stripe/plans. */
const PLAN_CHROME: Record<string, { name: string; desc: string; color: string; popular: boolean }> = {
  starter: { name: "Starter", desc: "Core HRIS for small teams",        color: SKY,  popular: false },
  growth:  { name: "Growth",  desc: "Core HRIS for growing teams",      color: BLUE, popular: true  },
  pro:     { name: "Pro",     desc: "Core HRIS for larger organisations", color: NAVY, popular: false },
};

const PLAN_ORDER = ["starter", "growth", "pro"];

/** Core-owned meters, in the order the plan card lists them. */
const CORE_CARD_METERS: { key: string; icon: string; label: (n: number) => string }[] = [
  { key: "ai_operation", icon: "🤖", label: n => `${n.toLocaleString()} AI operations / month` },
  { key: "ai_doc",       icon: "📝", label: n => `${n.toLocaleString()} AI documents / month` },
];

/**
 * Prose for each meter, keyed by the meter name in billing.py.
 *
 * The label and the owning module are NOT here — those come from the API, so a
 * meter that changes hands between modules changes on this page without an edit.
 * `employees` has no METER_LABELS entry (it is a cap, not a consumption meter),
 * hence the special case in the filter below.
 */
const METER_EXPLAINERS: { key: string; icon: string; desc: string; examples: string }[] = [
  {
    key: "employees", icon: "👥",
    desc: "Active employees in your workspace, including those with a pending invite. The one meter that bills overage rather than blocking.",
    examples: "Invite, bulk import",
  },
  {
    key: "ai_operation", icon: "🤖",
    desc: "Any AI query across the platform — Aria the HR assistant, AI suggestions, and vendor-bill reading with the Finance module.",
    examples: "Aria chat, AI analysis, vendor-bill OCR",
  },
  {
    key: "ai_doc", icon: "📝",
    desc: "Any AI-generated structured document. Each generation uses one from your monthly allowance.",
    examples: "Job descriptions, offer letters, HR letters",
  },
  {
    key: "cv_processed", icon: "📄",
    desc: "Each resume uploaded and AI-scored. Viewing or downloading an already-scored CV does not count.",
    examples: "Email intake, manual upload, CV re-scoring",
  },
  {
    key: "screening_interviews", icon: "🎥",
    desc: "Each AI video screening interview a candidate completes, scored answer by answer.",
    examples: "Async video screening",
  },
  {
    key: "payslips_issued", icon: "💸",
    desc: "Each payslip issued in a pay run. This meter never blocks a pay run — going past the included amount bills overage instead.",
    examples: "Issue, issue-all",
  },
  {
    key: "esign_requests_sent", icon: "✍️",
    desc: "Each signature request sent, including one per recipient in a bulk send. Signing, declining and reminders are free.",
    examples: "Send for signature, bulk send",
  },
  {
    key: "expense_captures_processed", icon: "🧾",
    desc: "Each receipt photographed and read by AI into a filed expense.",
    examples: "Receipt capture, bulk receipt upload",
  },
];

const FAQS = [
  {
    q: "What do I get without buying any modules?",
    a: "Core — the HRIS. Employee records and org chart, leave, attendance and timesheets, expense claims, assets, offboarding, AI-drafted HR letters, policy search, Aria the AI HR assistant, announcements, helpdesk and reports. Every employee can also view their own payslips. Core is included in every plan and is never gated.",
  },
  {
    q: "So what are modules?",
    a: "Six optional add-ons — Recruitment, Finance, Payroll, eSign, AI Expense Capture and Performance. You buy only the ones you need, each at a price that scales with your plan tier. Add or remove them at any time from Settings; adding is prorated, removing keeps access until the end of the period you already paid for.",
  },
  {
    q: "What happens to my quota when I add a module?",
    a: "Each module brings its own allowance on top of Core's. Recruitment adds your CVs processed and screening interviews, Payroll adds payslips issued, eSign adds signature requests, AI Expense Capture adds expense captures, and Finance adds AI operations for vendor-bill reading. A meter you have not bought the module for sits at zero.",
  },
  {
    q: "What counts as an AI operation?",
    a: "Any AI-powered query across the platform: Aria the HR assistant, AI suggestions, and — with the Finance module — reading a vendor bill. Generating a document (job description, offer letter, HR letter) counts as an AI document, not an AI operation.",
  },
  {
    q: "What counts as an AI document?",
    a: "Any AI-generated structured document: job descriptions, offer letters, employment letters, appointment letters, and other HR letters. Each generation uses one AI document from your monthly allowance. AI documents are a Core allowance, and the Recruitment module adds more.",
  },
  {
    q: "What counts as a CV processed?",
    a: "Each resume uploaded and AI-scored counts as one. Downloading or viewing an already-scored CV does not use quota. CVs processed is a Recruitment module allowance — without that module the meter is zero.",
  },
  {
    q: "What happens if I hit a limit?",
    a: "You'll see a message explaining which limit was reached and when it resets (the 1st of next month). You can add the relevant module or move up a tier at any time for more capacity immediately.",
  },
  {
    q: "What happens if I exceed my included employee count?",
    a: "Extra employees are billed at your plan's overage rate and charged automatically to your card at the end of each month — no manual action needed. If you hit the plan maximum, you'll need to move up a tier to add more.",
  },
  {
    q: "Is there a free trial?",
    a: "Yes — 14 days free at Growth-level limits with every module switched on, so you can see the whole product before deciding what to keep. Signing up authorizes your card but charges nothing until the trial ends. No company card handy? Request access instead and we'll set you up without one.",
  },
  {
    q: "Can I change plans or modules at any time?",
    a: "Yes. Moving up a tier applies immediately; moving down takes effect at the end of your billing cycle. Modules can be added or removed whenever you like from Settings.",
  },
  {
    q: "What happens to my data if I cancel?",
    a: "You can export all your data at any time. After cancellation, your workspace is archived for 30 days before deletion.",
  },
  {
    q: "Do you offer discounts for startups or NGOs?",
    a: "Yes. Contact us at contactus@sequencetechno.com and we'll work something out.",
  },
];

export default function PricingPage() {
  const [annual, setAnnual] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  // Prices, allowances and which modules are sellable all come from the backend.
  // This page hardcoded them until 2026-07-29 and spent months advertising
  // $129/$299/$649 against a real price of $79/$179/$399.
  const { data: plansData } = useQuery({
    queryKey: ["stripe-plans"], queryFn: getStripePlans,
    staleTime: 5 * 60 * 1000, retry: false,
  });
  const plans: any[] = plansData?.plans ?? [];
  const modulePrices = plansData?.module_prices ?? {};
  const meters: Record<string, { label: string; module: string | null }> = plansData?.meters ?? {};

  // Which modules are actually purchasable, per tier — `plansData.modules` is
  // purchasable_modules(), i.e. "has a resolvable Stripe price ID".
  //
  // NOT module_prices: that is the display dollar amount from billing.py and is
  // populated whether or not a Stripe price exists. Advertising from it is the exact
  // mistake price_book.py was written to prevent — an earlier attempt shipped module
  // checkboxes with zero price vars configured, and a real prospect who picked an
  // add-on got a raw 400 at checkout. A module we cannot charge for is not offered.
  const purchasableByPlan: Record<string, ModuleKey[]> = plansData?.modules ?? {};
  const isPurchasable = (key: ModuleKey, plan: string) =>
    (purchasableByPlan[plan] ?? []).includes(key);

  const sellableModules: ModuleKey[] = MODULE_KEYS.filter(
    k => PLAN_ORDER.some(p => isPurchasable(k, p))
  );

  /** Display price, or null when this module cannot be sold at this tier. */
  const modulePrice = (key: ModuleKey, plan: string): number | null => {
    if (!isPurchasable(key, plan)) return null;
    const usd = modulePrices?.[key]?.[plan]?.[annual ? "annual" : "monthly"] ?? null;
    return usd === null ? null : annual ? Math.round(usd / 12) : usd;
  };

  /** Meters this module brings, for the "what you also get" line on a module card. */
  const metersForModule = (key: ModuleKey): string[] =>
    Object.entries(meters).filter(([, m]) => m.module === key).map(([, m]) => m.label);

  return (
    <div style={{ fontFamily: "var(--font-sans)", background: "#fff", minHeight: "100vh" }}>
      <style>{`
        * { box-sizing: border-box; margin: 0; padding: 0; }
        .nav-link { color: #475569; text-decoration: none; font-size: 14px; font-weight: 500; padding: 8px 14px; border-radius: 8px; transition: all 0.15s; }
        .nav-link:hover { color: ${NAVY}; background: ${LIGHT}; }
        .nav-link.active { color: ${NAVY}; font-weight: 700; }
        .cta-primary { background: ${NAVY}; color: #fff; text-decoration: none; font-weight: 700; font-size: 14px; padding: 10px 22px; border-radius: 8px; display: inline-block; transition: opacity 0.15s; cursor: pointer; border: none; }
        .cta-primary:hover { opacity: 0.88; }
        .cta-secondary { background: transparent; color: ${NAVY}; text-decoration: none; font-weight: 600; font-size: 14px; padding: 9px 20px; border-radius: 8px; border: 1.5px solid ${NAVY}; display: inline-block; transition: all 0.15s; }
        .cta-secondary:hover { background: ${LIGHT}; }
        @media (max-width: 768px) {
          .plans-grid { grid-template-columns: 1fr !important; }
          .modules-grid { grid-template-columns: 1fr !important; }
          .compare-table { font-size: 12px !important; }
          .hero-title { font-size: 32px !important; }
          .nav-links { display: none !important; }
          .nav-desktop-ctas { display: none !important; }
          .nav-hamburger { display: flex !important; }
          .ai-ops-grid { grid-template-columns: 1fr !important; }
          .enterprise-section h2 { font-size: 22px !important; }
        }
      `}</style>

      {/* Navbar */}
      <nav style={{ borderBottom: "1px solid #e2e8f0", background: "#fff", position: "sticky", top: 0, zIndex: 100 }}>
        <div style={{ maxWidth: 1140, margin: "0 auto", height: 64, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 32px" }}>
          <a href="/" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none" }}>
            <img src="/favicon.png" alt="HRxAI" style={{ width: 32, height: 32, borderRadius: 8, objectFit: "contain" }} />
            <span style={{ fontWeight: 800, fontSize: 17, color: NAVY }}>HRxAI</span>
          </a>
          <div className="nav-links" style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <a href="/#features" className="nav-link">Features</a>
            <a href="/#how-it-works" className="nav-link">How it works</a>
            <a href="/docs" className="nav-link">Docs</a>
            <a href="/pricing" className="nav-link active">Pricing</a>
          </div>
          <div className="nav-desktop-ctas" style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <a href="/login" className="cta-secondary" style={{ padding: "7px 18px", fontSize: 13 }}>Sign in</a>
            <a href="/signup" className="cta-primary">Get started free →</a>
          </div>
          {/* Hamburger — mobile only */}
          <button
            className="nav-hamburger"
            onClick={() => setMenuOpen(m => !m)}
            style={{ display: "none", background: "none", border: "none", cursor: "pointer", padding: 8, flexDirection: "column", gap: 5 }}
          >
            <span style={{ display: "block", width: 22, height: 2, background: NAVY, borderRadius: 2, transition: "all 0.2s", transform: menuOpen ? "rotate(45deg) translate(5px, 5px)" : "none" }} />
            <span style={{ display: "block", width: 22, height: 2, background: NAVY, borderRadius: 2, opacity: menuOpen ? 0 : 1, transition: "all 0.2s" }} />
            <span style={{ display: "block", width: 22, height: 2, background: NAVY, borderRadius: 2, transition: "all 0.2s", transform: menuOpen ? "rotate(-45deg) translate(5px, -5px)" : "none" }} />
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      {menuOpen && (
        <div style={{
          position: "fixed", top: 64, left: 0, right: 0, zIndex: 99,
          background: "#fff", borderBottom: "1px solid #e2e8f0",
          padding: "12px 16px", display: "flex", flexDirection: "column", gap: 4,
          boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
        }}>
          {[
            { href: "/#features", label: "Features" },
            { href: "/#how-it-works", label: "How it works" },
            { href: "/pricing", label: "Pricing" },
            { href: "/docs", label: "Docs" },
          ].map(l => (
            <a key={l.href} href={l.href} onClick={() => setMenuOpen(false)}
              style={{ padding: "10px 14px", borderRadius: 8, fontSize: 15, fontWeight: 500, color: "#0f172a", textDecoration: "none" }}>
              {l.label}
            </a>
          ))}
          <div style={{ borderTop: "1px solid #f1f5f9", marginTop: 8, paddingTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
            <a href="/login" className="cta-secondary" style={{ textAlign: "center" }}>Sign in</a>
            <a href="/signup" className="cta-primary" style={{ textAlign: "center" }}>Start free trial →</a>
          </div>
        </div>
      )}

      {/* Hero */}
      <div style={{ textAlign: "center", padding: "72px 24px 48px", background: `linear-gradient(180deg, ${LIGHT} 0%, #fff 100%)` }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "#fff", border: `1px solid ${SKY}40`, borderRadius: 20, padding: "5px 14px", marginBottom: 24 }}>
          <span style={{ fontSize: 12 }}>✨</span>
          <span style={{ fontSize: 12, fontWeight: 600, color: SKY }}>14-day free trial · Card authorized, not charged</span>
        </div>
        <h1 className="hero-title" style={{ fontSize: 44, fontWeight: 800, color: "#0f172a", marginBottom: 16, lineHeight: 1.15 }}>
          Core HRIS, plus only what you need
        </h1>
        <p style={{ fontSize: 18, color: "#64748b", maxWidth: 540, margin: "0 auto 36px" }}>
          Every plan includes the full Core HRIS. Add Recruitment, Finance, Payroll and
          more as separate modules — and drop them again whenever you like.
        </p>

        {/* Toggle */}
        <div style={{ display: "inline-flex", alignItems: "center", gap: 12, background: "#f1f5f9", borderRadius: 12, padding: "6px 8px" }}>
          <button onClick={() => setAnnual(false)} style={{
            padding: "8px 20px", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 14, fontWeight: 600,
            background: !annual ? "#fff" : "transparent",
            color: !annual ? NAVY : "#64748b",
            boxShadow: !annual ? "0 1px 4px rgba(0,0,0,0.08)" : "none",
            transition: "all 0.2s",
          }}>Monthly</button>
          <button onClick={() => setAnnual(true)} style={{
            padding: "8px 20px", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 14, fontWeight: 600,
            background: annual ? "#fff" : "transparent",
            color: annual ? NAVY : "#64748b",
            boxShadow: annual ? "0 1px 4px rgba(0,0,0,0.08)" : "none",
            display: "flex", alignItems: "center", gap: 8, transition: "all 0.2s",
          }}>
            Annual
            <span style={{ background: GREEN, color: "#fff", fontSize: 11, fontWeight: 700, padding: "2px 7px", borderRadius: 20 }}>Save 17%</span>
          </button>
        </div>
      </div>

      {/* How the pricing is shaped — the tier buys Core, modules are separate. */}
      <div style={{ textAlign: "center", padding: "0 24px 48px" }}>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 10, flexWrap: "wrap", justifyContent: "center",
          background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 12,
          padding: "10px 20px", fontSize: 14, color: "#166534", fontWeight: 600,
          maxWidth: 720, textAlign: "left",
        }}>
          <span style={{ fontSize: 18 }}>✓</span>
          <span>
            The tier sets your Core HRIS and how far it scales.
            <span style={{ fontWeight: 400, color: "#475569" }}>
              {" "}Modules are priced separately and scale with the tier you pick.
            </span>
          </span>
        </div>
      </div>

      {/* Plan cards — Core only. Module allowances belong to the module section below. */}
      <div className="plans-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 24, maxWidth: 1100, margin: "0 auto", padding: "0 24px 56px" }}>
        {plans.map(p => {
          const chrome = PLAN_CHROME[p.id] ?? { name: p.id, desc: "", color: BLUE, popular: false };
          const annualTotal = p.annual_price_usd;
          const cheapestModule = Math.min(
            ...sellableModules.map(k => modulePrice(k, p.id) ?? Infinity)
          );
          return (
            <div key={p.id} style={{
              border: chrome.popular ? `2px solid ${chrome.color}` : "1.5px solid #e2e8f0",
              borderRadius: 16, overflow: "hidden", position: "relative",
              boxShadow: chrome.popular ? `0 8px 32px ${chrome.color}20` : "0 2px 8px rgba(0,0,0,0.06)",
            }}>
              {chrome.popular && (
                <div style={{ background: chrome.color, color: "#fff", textAlign: "center", padding: "6px 0", fontSize: 12, fontWeight: 700, letterSpacing: "0.06em" }}>
                  MOST POPULAR
                </div>
              )}
              <div style={{ padding: "28px 28px 24px" }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: chrome.color, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.06em" }}>{chrome.name}</div>
                <div style={{ fontSize: 13, color: "#64748b", marginBottom: 20 }}>{chrome.desc}</div>

                <div style={{ display: "flex", alignItems: "flex-end", gap: 6, marginBottom: 4 }}>
                  <span style={{ fontSize: 42, fontWeight: 800, color: "#0f172a", lineHeight: 1 }}>
                    ${annual ? Math.round(annualTotal / 12) : p.monthly_price_usd}
                  </span>
                  <span style={{ fontSize: 14, color: "#94a3b8", marginBottom: 8 }}>/month</span>
                </div>
                {annual ? (
                  <div style={{ fontSize: 12, color: GREEN, fontWeight: 600, marginBottom: 4 }}>
                    Billed ${annualTotal.toLocaleString()}/yr — saves ${(p.monthly_price_usd * 12 - annualTotal).toLocaleString()}
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 4 }}>Billed monthly</div>
                )}
                <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 24 }}>14-day free trial included</div>

                <a href={`/signup?plan=${p.id}`} className="cta-primary" style={{
                  display: "block", textAlign: "center", width: "100%",
                  background: chrome.popular ? chrome.color : "transparent",
                  color: chrome.popular ? "#fff" : chrome.color,
                  border: `2px solid ${chrome.color}`,
                  marginBottom: 8, padding: "11px",
                }}>Start free trial</a>
                <a href="/request-access" style={{
                  display: "block", textAlign: "center", fontSize: 12, color: "#94a3b8",
                  textDecoration: "underline", marginBottom: 24,
                }}>No company card? Request access</a>

                {/* Core allowances only. A CV or payslip number here is what made the
                    old card read as "Growth includes an ATS". */}
                <div style={{ borderTop: "1px solid #f1f5f9", paddingTop: 20, marginBottom: 20 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>
                    Core, every month
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                    <span style={{ fontSize: 14, flexShrink: 0 }}>👥</span>
                    <span style={{ fontSize: 13, color: "#334155", fontWeight: 500 }}>
                      {p.included_employees} employees included
                    </span>
                  </div>
                  {CORE_CARD_METERS.map(m => (
                    p.core_limits?.[m.key] > 0 && (
                      <div key={m.key} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                        <span style={{ fontSize: 14, flexShrink: 0 }}>{m.icon}</span>
                        <span style={{ fontSize: 13, color: "#334155", fontWeight: 500 }}>{m.label(p.core_limits[m.key])}</span>
                      </div>
                    )
                  ))}
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 4, padding: "6px 10px", background: "#f0fdf4", borderRadius: 6 }}>
                    <span style={{ fontSize: 13, color: "#166534", fontWeight: 500 }}>
                      +${p.overage_rate_usd} / extra employee beyond included
                    </span>
                  </div>
                </div>

                <div style={{
                  background: "#f8fafc", borderRadius: 8, padding: "10px 12px",
                  fontSize: 12, color: "#475569", display: "flex", gap: 8, alignItems: "flex-start",
                }}>
                  <span style={{ color: GREEN, fontWeight: 700, flexShrink: 0 }}>✓</span>
                  <span>
                    Full Core HRIS included.
                    {Number.isFinite(cheapestModule) && ` Add modules from $${cheapestModule}/mo.`}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Core — what every plan includes without buying anything else */}
      <div style={{ maxWidth: 900, margin: "0 auto", padding: "0 24px 56px" }}>
        <div style={{ textAlign: "center", marginBottom: 8 }}>
          <span style={{
            display: "inline-block", background: NAVY, color: "#fff", borderRadius: 999,
            padding: "4px 14px", fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
          }}>Included in every plan</span>
        </div>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: "#0f172a", marginBottom: 6, textAlign: "center" }}>
          {CORE_LABEL} — the Core HRIS
        </h2>
        <p style={{ fontSize: 14, color: "#64748b", textAlign: "center", marginBottom: 28 }}>
          Never gated, never an add-on. The tier only changes how far it scales.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 10 }}>
          {CORE_FEATURES.map(f => (
            <div key={f} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: "#f8fafc", borderRadius: 8, fontSize: 13, color: "#334155" }}>
              <span style={{ color: GREEN, fontWeight: 700, flexShrink: 0 }}>✓</span>
              {f}
            </div>
          ))}
        </div>
      </div>

      {/* Modules — priced separately, per AC1 of module-selection-signup.
          Absent entirely when nothing is purchasable, rather than a heading over an
          empty grid: a misconfigured price book must degrade to "we don't sell
          add-ons" and never to a module the checkout will refuse. */}
      {sellableModules.length > 0 && (
      <div style={{ background: "#f8fafc", borderTop: "1px solid #e2e8f0", borderBottom: "1px solid #e2e8f0" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "56px 24px" }}>
          <h2 style={{ fontSize: 26, fontWeight: 800, color: "#0f172a", marginBottom: 8, textAlign: "center" }}>
            Add only the modules you need
          </h2>
          <p style={{ fontSize: 15, color: "#64748b", textAlign: "center", marginBottom: 36, maxWidth: 620, marginLeft: "auto", marginRight: "auto" }}>
            Each module carries its own features and its own monthly allowance. Prices scale
            with your tier, and you can add or drop a module at any time from Settings.
          </p>

          <div className="modules-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 20 }}>
            {sellableModules.map(key => {
              const extraMeters = metersForModule(key);
              return (
                <div key={key} style={{
                  background: "#fff", border: "1.5px solid #e2e8f0", borderRadius: 14,
                  padding: "22px 22px 18px", display: "flex", flexDirection: "column",
                  borderTop: `4px solid ${MODULE_COLORS[key]}`,
                }}>
                  <div style={{ fontSize: 16, fontWeight: 800, color: "#0f172a", marginBottom: 4 }}>
                    {MODULE_LABELS[key]}
                  </div>
                  <div style={{ fontSize: 13, color: "#64748b", marginBottom: 16, lineHeight: 1.5 }}>
                    {MODULE_PITCH[key]}
                  </div>

                  {/* Per-tier price. Modules scale with the tier, so a single number
                      would be wrong for two thirds of readers. */}
                  <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                    {PLAN_ORDER.map(planId => {
                      const usd = modulePrice(key, planId);
                      if (usd === null) return null;
                      return (
                        <div key={planId} style={{
                          flex: 1, background: "#f8fafc", borderRadius: 8,
                          padding: "8px 6px", textAlign: "center",
                        }}>
                          <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                            {PLAN_CHROME[planId]?.name ?? planId}
                          </div>
                          <div style={{ fontSize: 15, fontWeight: 800, color: "#0f172a" }}>
                            ${usd}<span style={{ fontSize: 10, color: "#94a3b8", fontWeight: 500 }}>/mo</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: 7, marginBottom: 14 }}>
                    {MODULE_FEATURES[key].map(f => (
                      <div key={f} style={{ display: "flex", gap: 8, fontSize: 13, color: "#334155", lineHeight: 1.5 }}>
                        <span style={{ color: MODULE_COLORS[key], fontWeight: 700, flexShrink: 0 }}>✓</span>
                        {f}
                      </div>
                    ))}
                  </div>

                  {extraMeters.length > 0 && (
                    <div style={{
                      marginTop: "auto", paddingTop: 12, borderTop: "1px solid #f1f5f9",
                      fontSize: 12, color: "#64748b",
                    }}>
                      <span style={{ fontWeight: 600, color: "#475569" }}>Adds quota for: </span>
                      {extraMeters.join(", ")}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      )}

      {/* Enterprise */}
      <div style={{ background: NAVY, padding: "48px 24px", textAlign: "center" }}>
        <div style={{ maxWidth: 600, margin: "0 auto" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: SKY, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 12 }}>Enterprise</div>
          <h2 className="enterprise-section" style={{ fontSize: 28, fontWeight: 800, color: "#fff", marginBottom: 12 }}>500+ employees?</h2>
          <p style={{ fontSize: 16, color: "#94a3b8", marginBottom: 32 }}>
            Custom limits on everything, dedicated support, SLA guarantees, on-premise deployment, and volume pricing.
          </p>
          <a
            href="mailto:contactus@sequencetechno.com?subject=Enterprise%20Enquiry"
            onClick={e => { e.preventDefault(); window.location.href = "mailto:contactus@sequencetechno.com?subject=Enterprise%20Enquiry"; }}
            className="cta-primary"
            style={{ background: SKY, border: "none", padding: "13px 32px", fontSize: 15 }}
          >
            Talk to us →
          </a>
          <div style={{ marginTop: 16, fontSize: 14, color: "#94a3b8" }}>
            or email us directly at{" "}
            <a href="mailto:contactus@sequencetechno.com" style={{ color: SKY, textDecoration: "underline" }}>
              contactus@sequencetechno.com
            </a>
          </div>
        </div>
      </div>

      {/* Usage meters explainer. Which module owns which meter comes from the API
          (METER_MODULE), so this section cannot fall out of step with billing the way
          the old hardcoded "four simple meters" copy did once there were seven. */}
      <div style={{ maxWidth: 860, margin: "0 auto", padding: "72px 24px 0" }}>
        <h2 style={{ fontSize: 26, fontWeight: 800, color: "#0f172a", marginBottom: 8, textAlign: "center" }}>How usage limits work</h2>
        <p style={{ fontSize: 15, color: "#64748b", textAlign: "center", marginBottom: 40 }}>
          Every meter resets on the 1st of the month. Core's allowances come with the plan;
          the rest arrive with the module that owns them.
        </p>
        <div className="ai-ops-grid" style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 16, marginBottom: 16 }}>
          {METER_EXPLAINERS.filter(m => m.key === "employees" || meters[m.key]).map(item => {
            const raw = item.key === "employees" ? null : meters[item.key]?.module;
            // A module key the backend knows and this build does not — a seventh
            // module reaching a client still holding a cached bundle — would index
            // MODULE_COLORS/LABELS to undefined and render an empty, uncoloured pill.
            // Treating it as unrecognised keeps the badge legible; the meter itself
            // still gets described, which is the part a reader is here for.
            const owner: ModuleKey | null = raw && raw in MODULE_LABELS ? (raw as ModuleKey) : null;
            return (
              <div key={item.key} style={{ background: LIGHT, borderRadius: 12, padding: "20px 22px", border: "1px solid #e0f2fe" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 24 }}>{item.icon}</span>
                  <span style={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>
                    {item.key === "employees" ? "Employees" : meters[item.key].label}
                  </span>
                  <span style={{
                    fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em",
                    borderRadius: 999, padding: "2px 8px",
                    background: owner ? `${MODULE_COLORS[owner]}15` : "#e2e8f0",
                    color: owner ? MODULE_COLORS[owner] : "#475569",
                  }}>
                    {owner ? MODULE_LABELS[owner] : "Core"}
                  </span>
                </div>
                <p style={{ fontSize: 13, color: "#475569", marginBottom: 8, lineHeight: 1.6 }}>{item.desc}</p>
                <div style={{ fontSize: 12, color: "#94a3b8" }}>
                  <span style={{ fontWeight: 600, color: "#64748b" }}>Counts: </span>{item.examples}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* FAQ */}
      <div style={{ maxWidth: 720, margin: "0 auto", padding: "72px 24px" }}>
        <h2 style={{ fontSize: 26, fontWeight: 800, color: "#0f172a", marginBottom: 40, textAlign: "center" }}>Frequently asked questions</h2>
        {FAQS.map((faq, i) => (
          <div key={i} style={{ borderBottom: "1px solid #e2e8f0" }}>
            <button
              onClick={() => setOpenFaq(openFaq === i ? null : i)}
              style={{ width: "100%", textAlign: "left", padding: "20px 0", background: "none", border: "none", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16 }}
            >
              <span style={{ fontSize: 15, fontWeight: 600, color: "#0f172a" }}>{faq.q}</span>
              <span style={{ fontSize: 18, color: "#64748b", flexShrink: 0, transition: "transform 0.2s", transform: openFaq === i ? "rotate(45deg)" : "rotate(0)" }}>+</span>
            </button>
            {openFaq === i && (
              <div style={{ fontSize: 14, color: "#475569", lineHeight: 1.7, paddingBottom: 20 }}>{faq.a}</div>
            )}
          </div>
        ))}
      </div>

      {/* Footer CTA */}
      <div style={{ background: LIGHT, borderTop: "1px solid #e0f2fe", textAlign: "center", padding: "64px 24px" }}>
        <h2 style={{ fontSize: 28, fontWeight: 800, color: "#0f172a", marginBottom: 12 }}>Start your 14-day free trial</h2>
        {/* Was "No credit card required", which the signup flow contradicts — checkout
            authorizes a card (payment_method_collection="always") and charges it when
            the trial ends. /request-access is the genuinely card-free route. */}
        <p style={{ fontSize: 16, color: "#64748b", marginBottom: 32 }}>
          Every module switched on. Card authorized, not charged. Cancel anytime.
        </p>
        <a href="/signup" className="cta-primary" style={{ padding: "13px 36px", fontSize: 16, boxShadow: `0 4px 16px ${NAVY}30` }}>Get started free →</a>
      </div>

      {/* Simple footer */}
      <div style={{ borderTop: "1px solid #e2e8f0", padding: "24px 32px", display: "flex", justifyContent: "space-between", alignItems: "center", color: "#94a3b8", fontSize: 13 }}>
        <span>© 2026 HRxAI. All rights reserved.</span>
        <div style={{ display: "flex", gap: 24 }}>
          <a href="/docs" style={{ color: "#94a3b8", textDecoration: "none" }}>Docs</a>
          <a href="mailto:contactus@sequencetechno.com" style={{ color: "#94a3b8", textDecoration: "none" }}>Contact</a>
        </div>
      </div>
    </div>
  );
}
